import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";

/**
 * Splash — public landing page.
 * If the user is already authenticated, redirect straight to the dashboard.
 * Otherwise, show the full marketing hero (brand, career manifesto, signature,
 * credits, IMDb link) and CTAs.
 */
export default function Splash() {
  const { user, lang } = useAuth();
  const navigate = useNavigate();
  const t = (k) => tr(k, lang);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  if (user) return null;

  return (
    <div className="min-h-[80vh] relative flex flex-col justify-center py-16" data-testid="splash-hero">
      <div className="relative max-w-3xl">
        <div className="mono text-[11px] tracking-[0.4em] text-amber-500/80 uppercase" data-testid="splash-brand">
          {t("splash.eyebrow")}
        </div>

        {/* Hero manifesto — moved here from Dashboard so it lives on the
            PUBLIC landing page and disappears after login. */}
        <h1 className="serif text-3xl sm:text-4xl md:text-5xl font-semibold text-amber-400 tracking-wide leading-tight mt-6 whitespace-pre-line" data-testid="splash-manifesto-headline">
          {t("dash.manifesto_headline")}
        </h1>
        <p className="text-stone-300 mt-6 leading-relaxed text-base sm:text-lg" data-testid="splash-manifesto-body">
          {t("dash.manifesto_body")}
        </p>

        {/* 3 points */}
        <ol className="mt-8 flex flex-col gap-6 list-none" data-testid="splash-points">
          {[1, 2, 3].map((n) => (
            <li key={n} className="flex gap-4" data-testid={`splash-point-${n}`}>
              <span className="mono text-amber-500/80 text-sm shrink-0 mt-0.5 tracking-wider">
                {String(n).padStart(2, "0")}
              </span>
              <div>
                <div className="mono text-[12px] tracking-[0.22em] text-amber-400 uppercase" data-testid={`splash-point-${n}-title`}>
                  {t(`splash.point${n}_title`)}
                </div>
                <p className="text-stone-300 mt-1.5 leading-relaxed text-base" data-testid={`splash-point-${n}-body`}>
                  {t(`splash.point${n}_body`)}
                </p>
              </div>
            </li>
          ))}
        </ol>

        <p className="text-stone-200 mt-8 leading-relaxed text-base sm:text-lg" data-testid="splash-tagline">
          {t("splash.tagline_below_points")}
        </p>

        <p className="text-stone-200 italic mt-8 serif text-lg sm:text-xl">
          {t("dash.manifesto_imagine")}
        </p>
        <p className="text-amber-300/90 mt-3 serif text-xl sm:text-2xl">
          {t("dash.manifesto_cta")}
        </p>

        {/* Signature block */}
        <div className="mt-8 flex flex-col gap-1" data-testid="splash-signature-block">
          <div className="text-stone-400 text-sm mb-2" data-testid="splash-credibility-line">
            {t("splash.credibility_line")}
          </div>
          <div className="signature text-3xl sm:text-4xl text-stone-100 leading-none" data-testid="splash-signature-name">
            Marcin Zarzeczny
          </div>
          <div className="text-stone-400 text-sm mt-2" data-testid="splash-credits">
            The Agency, Out there, Supernova
            <span className="mx-2 text-stone-600">·</span>
            <a
              href="https://www.imdb.com/name/nm3515423/?ref_=ext_shr_lnk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-stone-300 underline underline-offset-2 hover:text-amber-500 transition-colors"
              data-testid="splash-imdb-link"
            >
              IMDb ↗
            </a>
          </div>
        </div>

        {/* CTAs */}
        <div className="mt-10 flex flex-wrap gap-3">
          <button
            data-testid="splash-cta-register"
            className="pill-btn"
            onClick={() => navigate("/login?mode=register")}
          >
            {t("splash.start")}
          </button>
          <button
            data-testid="splash-cta-login"
            className="ghost-btn"
            onClick={() => navigate("/login")}
          >
            {t("splash.login") || t("nav.login") || "Zaloguj się"}
          </button>
          <button
            data-testid="splash-cta-pricing"
            className="ghost-btn"
            onClick={() => navigate("/pricing")}
          >
            {t("splash.pricing")}
          </button>
        </div>
      </div>
    </div>
  );
}
